import {
  createRootRoute,
  createRoute,
  createRouter,
  lazyRouteComponent,
  notFound,
  redirect,
} from '@tanstack/react-router'

import { pages, detailPages } from './pages'
import { entitySearchSchema, parseSearch, stringifySearch } from './search'
import { NotFoundErrorPage } from '@/Components/NotFoundErrorPage'
import { createTokenProvider } from '@/lib/auth/tokenProvider'
import { authAndInstanceRedirect } from '@/lib/query/queryClient'

const rootRoute = createRootRoute({
  component: lazyRouteComponent(() => import('@/App'), 'App'),
  notFoundComponent: NotFoundErrorPage,
  errorComponent: NotFoundErrorPage,
})
const instanceRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/i/$instanceId',
  validateSearch: entitySearchSchema,
  beforeLoad: async ({ params }) => {
    if (!/^[a-f0-9]{32}$/.test(params.instanceId)) throw notFound()
    try {
      await createTokenProvider(params.instanceId).get()
    } catch (error) {
      const href = authAndInstanceRedirect(error)
      if (href) throw redirect({ href, reloadDocument: true })
      throw error
    }
  },
  component: lazyRouteComponent(
    () => import('@/Components/AppLayout/AppLayout'),
    'AppLayout',
  ),
})
const indexRoute = createRoute({
  getParentRoute: () => instanceRoute,
  path: '/',
  beforeLoad: ({ params, search }) => {
    throw redirect({
      to: '/i/$instanceId/dashboard',
      params,
      search,
      replace: true,
    })
  },
})
const CrmPage = lazyRouteComponent(() => import('@/pages/CrmPage'), 'CrmPage')
function createPageRoute<TPath extends string>(page: { path: TPath }) {
  return createRoute({
    getParentRoute: () => instanceRoute,
    path: page.path,
    component: CrmPage,
  })
}
const pageRoutes = pages.map(createPageRoute)
const detailsRoutes = detailPages.map(createPageRoute)
// Previously published language URLs remain usable; language is now owned by i18next.
const languageRoute = createRoute({
  getParentRoute: () => instanceRoute,
  path: '/$language/{-$page}',
  beforeLoad: async ({ params, search }) => {
    if (!['en', 'de', 'fr', 'pl'].includes(params.language)) throw notFound()
    const { default: i18n } = await import('i18next')
    await i18n.changeLanguage(params.language)
    throw redirect({
      href: `/i/${params.instanceId}/${params.page || 'dashboard'}${stringifySearch(search)}`,
      replace: true,
    })
  },
})

const routeTree = rootRoute.addChildren([
  instanceRoute.addChildren([
    indexRoute,
    ...pageRoutes,
    ...detailsRoutes,
    languageRoute,
  ]),
])

export function createAppRouter() {
  return createRouter({
    routeTree,
    parseSearch,
    stringifySearch,
    defaultPreload: false,
    scrollRestoration: true,
    scrollToTopSelectors: ['main'],
  })
}

export const router = createAppRouter()

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}
